import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/indexRedux";
import { FormData, FormVolumes, ServiceSelection } from "../types/addServiceTypes";

export const useAddServiceForm = () => {
  const selectedUserCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);

  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = localStorage.getItem("addServiceFormData");
    const savedCompanyId = localStorage.getItem("addServiceCompanyId");

    // Check if company has changed
    if (
      savedCompanyId &&
      selectedUserCompany?.company_id &&
      savedCompanyId !== selectedUserCompany.company_id.toString()
    ) {
      // Clear all related localStorage items
      localStorage.removeItem("addServiceFormData");
      localStorage.removeItem("formVolumes");
      localStorage.removeItem("selectedServices");
      localStorage.removeItem("contractOverview");
      localStorage.removeItem("pslData");
      localStorage.removeItem("pslFullData");
      localStorage.removeItem("requiresSeparateContracts");
      localStorage.removeItem("formVolumes");
      localStorage.removeItem("selectedServices");
      localStorage.setItem("addServiceCompanyId", selectedUserCompany.company_id.toString());
      localStorage.removeItem("installationNumbers");
      localStorage.removeItem("lastContractPayload");
      localStorage.removeItem("billingInfo");
      localStorage.removeItem("siteChoice");

      // Return default values
      return {
        ean: "",
        showTable: false,
        step: 1,
        highestStep: 1,
        technology: [],

        eanAdded: false,
      };
    }

    return savedData
      ? JSON.parse(savedData)
      : {
          ean: "",
          showTable: false,
          step: 1,
          highestStep: 1,
          technology: [],

          eanAdded: false,
        };
  });

  const [formVolumes, setFormVolumes] = useState<FormVolumes>(() => {
    const saved = localStorage.getItem("formVolumes");
    return saved
      ? JSON.parse(saved)
      : {
          annualInjection: null,
          annualInjectionUnit: "Mwh",
          annualOfftake: null,
          annualOfftakeUnit: "Mwh",
        };
  });

  const [selectedServices, setSelectedServices] = useState<ServiceSelection>(() => {
    const saved = localStorage.getItem("selectedServices");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { injection: false, offtake: false };
      }
    }
    return { injection: false, offtake: false };
  });

  // Track company changes
  useEffect(() => {
    if (selectedUserCompany?.company_id) {
      const savedCompanyId = localStorage.getItem("addServiceCompanyId");

      if (savedCompanyId && savedCompanyId !== selectedUserCompany.company_id.toString()) {
        // Company has changed - clear all form data
        localStorage.removeItem("addServiceFormData");
        localStorage.removeItem("formVolumes");
        localStorage.removeItem("selectedServices");
        localStorage.removeItem("siteChoice");

        // Reset state to defaults
        setFormData({
          ean: "",
          showTable: false,
          step: 1,
          highestStep: 1,
          technology: [],
          yearlyVolume: 8,
          eanAdded: false,
        });

        setFormVolumes({
          annualInjection: null,
          annualInjectionUnit: "Mwh",
          annualOfftake: null,
          annualOfftakeUnit: "Mwh",
        });

        setSelectedServices({ injection: false, offtake: false });
      }

      localStorage.setItem("addServiceCompanyId", selectedUserCompany.company_id.toString());
    }
  }, [selectedUserCompany?.company_id]);

  useEffect(() => {
    localStorage.setItem("addServiceFormData", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem("formVolumes", JSON.stringify(formVolumes));
  }, [formVolumes]);

  useEffect(() => {
    localStorage.setItem("selectedServices", JSON.stringify(selectedServices));
  }, [selectedServices]);

  return {
    formData,
    setFormData,
    formVolumes,
    setFormVolumes,
    selectedServices,
    setSelectedServices,
    selectedUserCompany,
  };
};
